/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

import { css, jsx } from '@atlaskit/css';
import { Text } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';
import { ProviderIconView } from '@atlassian/conversation-assistant-utils/ProviderIconView';
import { useSmartLinkMetadata } from '@atlassian/conversation-assistant-utils/useSmartLinkMetadata';

const providerIconWrapperStyles = css({
	display: 'flex',
	alignItems: 'center',
	flexShrink: 0,
});

const resolvedRowStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.100', '8px'),
	minWidth: '0px',
	overflow: 'hidden',
});

type SmartLinkSourceRowProps = {
	/** The URL to resolve via the SmartLink store. */
	url: string;
	/** Shown while resolving or when resolution fails. */
	fallback: string;
	/**
	 * When provided, overrides the resolved SmartLink title with a user-supplied
	 * display name (e.g. the value entered in the link picker's display text field).
	 */
	displayTextOverride?: string;
	/**
	 * Called with `true` while the SmartLink is resolving and `false` once resolved.
	 * Use this to hide delete buttons or other actions until the item is fully loaded.
	 */
	onLoadingChange?: (isLoading: boolean) => void;
};

const skeletonRowStyles = css({
	flex: '1 1 0px',
	width: '100%',
	minWidth: '0px',
	overflow: 'hidden',
});

const titleStyles = css({
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	minWidth: '0px',
});

/**
 * Renders the provider icon and title for a URL by resolving it via the SmartLink store.
 * Falls back to `fallback` while pending or on error.
 *
 * Requires a <SmartCardProvider> in the tree.
 */
export const SmartLinkSourceRow = ({
	url,
	fallback,
	displayTextOverride,
	onLoadingChange,
}: SmartLinkSourceRowProps): React.JSX.Element => {
	const { title, providerIcon, status } = useSmartLinkMetadata(url);

	const isLoading = status === 'pending';

	// Notify parent of loading state changes
	React.useEffect(() => {
		onLoadingChange?.(isLoading);
	}, [isLoading, onLoadingChange]);

	if (isLoading) {
		return (
			<div css={skeletonRowStyles}>
				<Skeleton width="100%" height="20px" isShimmering />
			</div>
		);
	}

	// Priority: user override > resolved SmartLink title > fallback name
	const displayedTitle = displayTextOverride || title || fallback;

	return (
		<div css={resolvedRowStyles}>
			{/* Wrap in a flex-shrink:0 container so icons don't compress in the flex row */}
			{providerIcon && (
				<span css={providerIconWrapperStyles}>
					<ProviderIconView icon={providerIcon} />
				</span>
			)}
			<span css={titleStyles}>
				<Text maxLines={1}>{displayedTitle}</Text>
			</span>
		</div>
	);
};
